/**
 * ─── FEATURE MAP ───────────────────────────────────────────────────
 * Single source of truth for route → feature(s) mapping.
 * Used by: sidebar, middleware, API route guards.
 *
 * Each entry maps a pathname prefix to one or more feature codes.
 * Access is granted when the user has ALL listed features (AND logic).
 * If a route is not listed here it is considered public/unprotected.
 * ────────────────────────────────────────────────────────────────────
 */

export type FeatureCode =
  | "pos.sales"
  | "pos.products"
  | "pos.inventory"
  | "pos.users"
  | "cash"
  | "warehouse_basic"
  | "warehouse_counts"
  | "warehouse_transfers"
  | "settings"
  | "reports"
  | "quotes"
  | "quotes_basic"
  | "quotes_convert"
  | "quotes_crm"
  | "suppliers";

/* ─── PAGE ROUTES ──────────────────────────────────────────────── */

export type RouteFeatureEntry = {
  /** Path prefix to match (without trailing slash) */
  path: string;
  /** Feature code(s) required – ALL must be enabled */
  features: FeatureCode[];
  /** If true, match only exact path (not sub-paths) */
  exact?: boolean;
};

/**
 * Ordered from most-specific to least-specific so the first match wins.
 * More specific sub-paths MUST come before their parent.
 */
export const ROUTE_FEATURE_MAP: RouteFeatureEntry[] = [
  // Almacén sub-routes (more specific first)
  { path: "/almacen/conteos", features: ["warehouse_counts"] },
  { path: "/almacen/transferencias", features: ["warehouse_transfers"] },
  { path: "/almacen", features: ["warehouse_basic"] },

  // Main modules
  { path: "/venta", features: ["pos.sales"] },
  { path: "/caja", features: ["cash"] },
  { path: "/reportes", features: ["reports"] },
  { path: "/productos", features: ["pos.products"] },   // Product catalog (Free plan can see)
  { path: "/proveedores", features: ["suppliers"] },
  { path: "/cotizaciones", features: ["quotes"] },
  { path: "/config", features: ["settings"] },
];

/**
 * Given a pathname, return the required feature codes or null if unprotected.
 */
export function getFeaturesForPath(pathname: string): FeatureCode[] | null {
  const normalized = pathname.replace(/\/+$/, "") || "/";

  for (const entry of ROUTE_FEATURE_MAP) {
    if (entry.exact) {
      if (normalized === entry.path) return entry.features;
    } else {
      if (normalized === entry.path || normalized.startsWith(entry.path + "/")) {
        return entry.features;
      }
    }
  }

  return null; // unprotected route
}

/* ─── API ROUTES ───────────────────────────────────────────────── */

export type ApiFeatureEntry = {
  /** API path prefix (after /api/) */
  path: string;
  /** Feature code(s) required – ANY of them grants access (OR logic) */
  features: FeatureCode[];
};

/**
 * Maps API route prefixes to required features.
 * Uses OR logic: having ANY of the listed features grants access.
 */
export const API_FEATURE_MAP: ApiFeatureEntry[] = [
  // Sales
  { path: "/api/sales", features: ["pos.sales"] },
  { path: "/api/register", features: ["pos.sales"] },

  // Cash register
  { path: "/api/cash-register", features: ["cash"] },

  // Inventory (counts need warehouse_counts OR warehouse_basic)
  { path: "/api/inventory/counts", features: ["warehouse_counts"] },
  { path: "/api/inventory/transfer", features: ["warehouse_transfers"] },
  { path: "/api/inventory/adjust", features: ["warehouse_basic", "pos.inventory"] },
  { path: "/api/inventory/movements", features: ["warehouse_basic", "pos.inventory"] },
  { path: "/api/inventory", features: ["warehouse_basic", "pos.inventory"] },

  // Reports
  { path: "/api/reports", features: ["reports"] },

  // Products (read is pos.products; write is gated separately in route handler)
  { path: "/api/products", features: ["pos.products"] },

  // Services (linked to sales)
  { path: "/api/services", features: ["pos.sales"] },

  // Customers (accessible from sales context)
  { path: "/api/customers", features: ["pos.sales"] },

  // Suppliers
  // { path: "/api/suppliers", features: ["suppliers"] },

  // Config / settings
  { path: "/api/branding", features: ["settings"] },
  { path: "/api/print/templates", features: ["settings"] },

  // Platform admin (no feature gate – handled by platform_admins table)
  // { path: "/api/platform", features: [] },
];

/**
 * Given an API pathname, return the required feature codes or null if unprotected.
 * Uses OR logic: user needs at least one of the returned features.
 */
export function getFeaturesForApi(pathname: string): FeatureCode[] | null {
  const normalized = pathname.replace(/\/+$/, "");

  for (const entry of API_FEATURE_MAP) {
    if (normalized === entry.path || normalized.startsWith(entry.path + "/")) {
      return entry.features;
    }
  }

  return null; // unprotected API route
}

/* ─── WRITE PERMISSIONS ─────────────────────────────────────────
 * Some features allow read but restrict writes by role.
 * These constants define which features grant write access.
 * Use in API route handlers for POST/PUT/DELETE operations.
 * ────────────────────────────────────────────────────────────── */

/**
 * Features that grant WRITE access to products (create/edit/delete).
 * Seller has pos.products (read) but NOT pos.inventory, so they can't mutate.
 * warehouse_worker has pos.inventory + pos.products → can manage stock.
 */
export const PRODUCTS_WRITE_FEATURES: FeatureCode[] = ["pos.inventory"];

/**
 * Checks if a set of resolved features allows product writes.
 * Seller can READ products (for the sales view) but NOT write.
 */
export function canWriteProducts(features: Record<string, boolean>): boolean {
  return PRODUCTS_WRITE_FEATURES.some((f) => features[f] === true);
}

/* ─── SIDEBAR HELPERS ──────────────────────────────────────────── */

/**
 * Given an href from NAV_ITEMS, return the feature codes needed,
 * or null if no gate is needed (e.g. dashboard).
 */
export function getRequiredFeaturesForNavItem(href: string): FeatureCode[] | null {
  return getFeaturesForPath(href);
}
